import { useState } from "react";
import { fetchDownloadUrl, useAuthToken, useReportsList } from "@/lib/hooks";
import { fmtDate } from "@/lib/format";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Reports() {
  const { data, isLoading, error } = useReportsList();
  const token = useAuthToken();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  async function download(key: string) {
    try {
      setBusyKey(key);
      const url = await fetchDownloadUrl(token, key);
      window.location.href = url;
    } finally {
      setBusyKey(null);
    }
  }

  function exportXls() {
    downloadXlsx(
      stampedName("reports-list"),
      ["File", "Type", "Size (bytes)", "Generated"],
      (data?.reports ?? []).map((r) => [
        r.key.split("/").pop() ?? r.key, r.kind, r.size, r.last_modified,
      ]),
    );
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="mt-1 text-sm text-ink-500">
            Generated daily and weekly Excel reports. Click to download.
          </p>
        </div>
        <DownloadButton onClick={exportXls} disabled={!data?.reports.length} label="Download list" />
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : !data?.reports.length ? (
            <div className="p-10 text-center text-sm text-ink-500">No reports yet.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-5 py-2 text-left">File</th>
                  <th className="px-5 py-2 text-left">Type</th>
                  <th className="px-5 py-2 text-right">Size</th>
                  <th className="px-5 py-2 text-right">Generated</th>
                  <th className="px-5 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {data.reports.map((r) => (
                  <tr key={r.key} className="hover:bg-ink-50">
                    <td className="px-5 py-2 font-medium text-ink-900">{r.key.split("/").pop()}</td>
                    <td className="px-5 py-2 text-ink-600 capitalize">{r.kind}</td>
                    <td className="px-5 py-2 text-right text-ink-500 tabular-nums">{fmtSize(r.size)}</td>
                    <td className="px-5 py-2 text-right text-ink-500">{fmtDate(r.last_modified)}</td>
                    <td className="px-5 py-2 text-right">
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={busyKey === r.key}
                        onClick={() => download(r.key)}
                      >
                        {busyKey === r.key ? "Preparing…" : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
